import { Check, Loader2, Search } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/lib/hooks';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  id: string;
  label: string;
  leading?: ReactNode;
}

export interface ComboboxPage<T extends ComboboxOption = ComboboxOption> {
  items: T[];
  hasMore: boolean;
}

export interface ComboboxFetchParams {
  search: string;
  page: number;
}

interface ComboboxProps<T extends ComboboxOption> {
  /** Current label shown in the field when closed. */
  value: string;
  /** Receives the whole selected option, so callers extract what they need. */
  onChange: (option: T) => void;
  /** Loads one page of options for a search term. Must be referentially stable. */
  fetchPage: (params: ComboboxFetchParams) => Promise<ComboboxPage<T>>;
  /** Namespace for the shared page cache (e.g. "projects"). */
  cacheKey: string;
  placeholder?: string;
  emptyLabel?: string;
}

/**
 * Shared page cache across combobox instances, keyed by `cacheKey:search:page`.
 * Reopening or retyping an earlier query is served from memory.
 */
const pageCache = new Map<string, ComboboxPage<ComboboxOption>>();

/**
 * Clears the shared page cache (all entries, or only a `cacheKey` namespace).
 * Call after creating/updating the underlying records so an open combobox
 * doesn't serve a stale page (e.g. a just-created artist).
 */
export function clearComboboxCache(cacheKey?: string): void {
  if (!cacheKey) {
    pageCache.clear();
    return;
  }
  const prefix = `${cacheKey}:`;
  for (const key of [...pageCache.keys()]) {
    if (key.startsWith(prefix)) pageCache.delete(key);
  }
}

/**
 * Searchable, server-driven combobox. Loads the first page on open and keeps
 * fetching as the list is scrolled (infinite scroll). The field is the search
 * input; only the matching list floats below it.
 */
export function Combobox<T extends ComboboxOption>({
  value,
  onChange,
  fetchPage,
  cacheKey,
  placeholder = 'Search',
  emptyLabel = 'No results.',
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef('');
  const debounced = useDebouncedValue(search, 250);
  const query = debounced.trim();

  // Load page 1 whenever the (debounced) search changes while open.
  useEffect(() => {
    if (!open) return;
    queryRef.current = query;

    const key = `${cacheKey}:${query.toLowerCase()}:1`;
    const cached = pageCache.get(key) as ComboboxPage<T> | undefined;
    if (cached) {
      setOptions(cached.items);
      setHasMore(cached.hasMore);
      setPage(1);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    fetchPage({ search: query, page: 1 })
      .then((result) => {
        if (!active) return;
        pageCache.set(key, result as ComboboxPage<ComboboxOption>);
        setOptions(result.items);
        setHasMore(result.hasMore);
        setPage(1);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, query, cacheKey, fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const next = page + 1;
    const current = queryRef.current;
    const key = `${cacheKey}:${current.toLowerCase()}:${next}`;

    const cached = pageCache.get(key) as ComboboxPage<T> | undefined;
    if (cached) {
      setOptions((prev) => [...prev, ...cached.items]);
      setHasMore(cached.hasMore);
      setPage(next);
      return;
    }

    setLoading(true);
    fetchPage({ search: current, page: next })
      .then((result) => {
        if (current !== queryRef.current) return;
        pageCache.set(key, result as ComboboxPage<ComboboxOption>);
        setOptions((prev) => [...prev, ...result.items]);
        setHasMore(result.hasMore);
        setPage(next);
      })
      .finally(() => setLoading(false));
  }, [loading, hasMore, page, cacheKey, fetchPage]);

  // If the first page doesn't overflow the list, keep loading until it does
  // (or there's nothing more), so the user can always reach later pages.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el && options.length > 0 && hasMore && !loading && el.scrollHeight <= el.clientHeight + 1) {
      loadMore();
    }
  }, [open, options, hasMore, loading, loadMore]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2">
        <Search className="size-4 text-muted-foreground" />
      </span>

      <Input
        value={open ? search : value}
        onChange={(event) => {
          setSearch(event.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setSearch('');
        }}
        placeholder={placeholder}
        className="pl-9"
        role="combobox"
        aria-expanded={open}
      />

      {open && (
        <div
          ref={listRef}
          onScroll={(event) => {
            const el = event.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMore();
          }}
          className="absolute top-full left-0 z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {options.length === 0 && !loading ? (
            <p className="py-6 text-center text-muted-foreground text-sm">{emptyLabel}</p>
          ) : (
            options.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onChange(item);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                  value === item.label && 'bg-accent',
                )}
              >
                {item.leading}
                <span className="flex-1 truncate">{item.label}</span>
                {value === item.label && <Check className="size-4 shrink-0" />}
              </button>
            ))
          )}

          {loading && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
